import SEO from '@/components/ui-custom/SEO';

const Privacy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy | Ard's Plumbing & Electric"
        description="Privacy Policy for Ard's Plumbing & Electric. Learn how we collect, use, and protect your personal information."
        canonical="/privacy-policy"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Privacy Policy', 'item': 'https://ardsdothan.com/privacy-policy' }] }}
      />

      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
            PRIVACY <span className="text-brand-yellow">POLICY</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we protect your information.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto prose prose-lg">
          
          <div className="mb-10 text-gray-700 pb-6 border-b border-gray-200">
            <p><strong>Effective Date:</strong> February 14, 2026</p>
            <p><strong>Last Updated:</strong> February 14, 2026</p>
            <p className="mt-4">
              Ard’s Plumbing and Electric (“Company,” “we,” “our,” or “us”) values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you interact with us, including through our website, phone calls, SMS messaging, AI receptionist system, and service platforms.
            </p>
            <p className="mt-4">
              By using our services or contacting us, you agree to the terms of this Privacy Policy.
            </p>
          </div>

          <div className="space-y-12">
            
            {/* 1. Information We Collect */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">1. Information We Collect</h2>
              
              <h3 className="font-semibold text-brand-black mt-6 mb-2">A. Personal Information</h3>
              <p className="text-gray-700">We may collect:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Full name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Service address</li>
                <li>Billing address</li>
                <li>Payment information</li>
                <li>Service history and job details</li>
                <li>Appointment preferences</li>
              </ul>

              <h3 className="font-semibold text-brand-black mt-6 mb-2">B. Call & AI Interaction Data</h3>
              <p className="text-gray-700">When you call our business, we may collect:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Call recordings</li>
                <li>Call transcripts</li>
                <li>Voicemail messages</li>
                <li>AI-generated summaries</li>
                <li>Information you provide during the call</li>
              </ul>

              <h3 className="font-semibold text-brand-black mt-6 mb-2">C. Automatically Collected Information</h3>
              <p className="text-gray-700">When you visit our website, we may collect:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device type</li>
                <li>Pages visited</li>
                <li>Cookies and tracking data</li>
              </ul>
            </div>

            {/* 2. AI Receptionist & Call Recording */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">2. AI Receptionist & Call Recording</h2>
              <p className="text-gray-700">Ard’s Plumbing and Electric uses an AI-powered receptionist and automated call-handling system to:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Answer incoming calls</li>
                <li>Collect customer information</li>
                <li>Schedule appointments</li>
                <li>Provide service details</li>
                <li>Record and transcribe calls</li>
                <li>Generate call summaries</li>
              </ul>
              <p className="text-gray-700">
                Calls may be recorded and transcribed for quality assurance, training, scheduling accuracy, and customer service purposes. By calling our business, you consent to automated processing, recording, and transcription of your call as described in this policy. Information collected through our AI system may be securely stored through our business management platforms, including Jobber, and related third-party service providers. We do not sell call data or transcripts.
              </p>
            </div>

            {/* 3. How We Use Your Information */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700">We use collected information to:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Provide plumbing and electrical services</li>
                <li>Schedule and manage appointments</li>
                <li>Send estimates, invoices, and receipts</li>
                <li>Process payments</li>
                <li>Provide customer support</li>
                <li>Send appointment reminders and service updates</li>
                <li>Improve operations</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud or abuse</li>
              </ul>
              <p className="text-gray-700 font-bold">We do not sell your personal information.</p>
            </div>

            {/* 4. SMS & Communication Policy */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">4. SMS & Communication Policy (10DLC / TCPA Compliance)</h2>
              <p className="text-gray-700">By providing your phone number, you consent to receive:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Appointment confirmations</li>
                <li>Appointment reminders</li>
                <li>Service updates</li>
                <li>Invoice notifications</li>
                <li>Customer support communications</li>
              </ul>
              <p className="text-gray-700">
                Message frequency varies. Standard message and data rates may apply. You may opt out at any time by replying STOP. For assistance, reply HELP or call (334) 692-5537. We do not share or sell phone numbers for third-party marketing.
              </p>
            </div>

            {/* 5. Third-Party Service Providers */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">5. Third-Party Service Providers</h2>
              <p className="text-gray-700">We use trusted third-party providers to operate our business, including:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li><strong>Jobber</strong> for scheduling, CRM, invoicing, and communication</li>
                <li>Payment processors integrated through Jobber</li>
                <li>AI call handling platforms</li>
                <li>Email and SMS providers</li>
                <li>Website hosting and analytics services</li>
              </ul>
              <p className="text-gray-700">
                These providers may process data on our behalf and are required to safeguard it.
              </p>
            </div>

            {/* 6. Payment Processing */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">6. Payment Processing</h2>
              <p className="text-gray-700">
                Payments are processed securely through third-party payment processors. Ard’s Plumbing and Electric does not store full credit card numbers on our servers.
              </p>
            </div>

            {/* 7. Data Security */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">7. Data Security</h2>
              <p className="text-gray-700">
                We implement reasonable administrative, technical, and physical safeguards to protect personal information. However, no system can guarantee absolute security.
              </p>
            </div>

            {/* 8. Data Retention */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">8. Data Retention</h2>
              <p className="text-gray-700">We retain information as long as necessary to:</p>
              <ul className="list-disc pl-6 mt-2 mb-4 space-y-2 text-gray-700">
                <li>Provide services</li>
                <li>Maintain business and tax records</li>
                <li>Resolve disputes</li>
                <li>Comply with legal requirements</li>
              </ul>
              <p className="text-gray-700">
                Call recordings and transcripts may be retained for documentation and operational purposes.
              </p>
            </div>

            {/* 9. Cookies & Tracking Technologies */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">9. Cookies & Tracking Technologies</h2>
              <p className="text-gray-700">
                Our website may use cookies or analytics tools to improve performance and user experience. You may disable cookies in your browser settings.
              </p>
            </div>

            {/* 10. Your Privacy Rights */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">10. Your Privacy Rights</h2>
              <p className="text-gray-700">You may request to:</p>
              <ul className="list-disc pl-6 mt-2 mb-6 space-y-2 text-gray-700">
                <li>Access your data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion (subject to legal obligations)</li>
              </ul>
              <p className="text-gray-700 mb-4">We may verify your identity before fulfilling requests.</p>
              
              <h3 className="font-semibold text-brand-black mt-6 mb-3">Contact us:</h3>
              <div className="bg-gray-50 p-6 rounded-xl text-gray-700 border border-gray-100">
                <p className="mb-2 flex items-center gap-2">
                  <span className="text-xl">📞</span> 
                  <a href="tel:3346925537" className="text-brand-coral hover:underline font-medium">(334) 692-5537</a>
                </p>
                <p className="mb-2 flex items-center gap-2">
                  <span className="text-xl">🌐</span> 
                  <a href="https://ardsdothan.com" className="text-brand-coral hover:underline font-medium" target="_blank" rel="noreferrer">ardsdothan.com</a>
                </p>
                <p className="mb-2 flex items-center gap-2">
                  <span className="text-xl">📧</span> 
                  <a href="mailto:Robert.ard@ardsdothan.com" className="text-brand-coral hover:underline font-medium">Robert.ard@ardsdothan.com</a>
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-xl">📍</span> 
                  <span>733 West State Hwy 92, Newton, Alabama 36352</span>
                </p>
              </div>
            </div>

            {/* 11. Children's Privacy */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">11. Children’s Privacy</h2>
              <p className="text-gray-700">
                Our services are not directed to individuals under 18. We do not knowingly collect data from minors.
              </p>
            </div>

            {/* 12. Legal Compliance */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">12. Legal Compliance</h2>
              <p className="text-gray-700">
                We may disclose information if required to comply with law enforcement, subpoenas, court orders, or to protect our legal rights.
              </p>
            </div>

            {/* 13. Limitation of Liability */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">13. Limitation of Liability</h2>
              <p className="text-gray-700">
                To the fullest extent permitted by law, Ard’s Plumbing and Electric shall not be liable for indirect or consequential damages arising from unauthorized data access beyond our reasonable control.
              </p>
            </div>

            {/* 14. Changes to This Policy */}
            <div>
              <h2 className="font-heading font-bold text-2xl text-brand-black mb-4">14. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy at any time. Updates will be posted with a revised date. Continued use of our services after changes are posted constitutes acceptance of the revised policy.
              </p>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default Privacy;